import React, { useState } from "react";
import DesignPreviewShell from "@/components/DesignPreviewShell";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * Field Radar × ContractorPlus — Property Detail Preview
 *
 * READ-ONLY VISUAL PROTOTYPE. Preview of a property/address detail page.
 * Inspired by CP's contact hero card + horizontal tabs, adapted for Field
 * Radar's actual PMR data model (WOs · Photos · Receipts · Tenants ·
 * Documents · Notes · Billing).
 *
 * Route: /admin/design-preview/property
 */
export default function PropertyDetailPreview() {
  const [tab, setTab] = useState("timeline");
  const { theme } = useDesignTheme();

  return (
    <DesignPreviewShell>
    <div className="font-sans">
      {/* ── HERO GRADIENT ────────────────────────────────────────── */}
      <div className={`${theme.gradient} pt-5 pb-24 sm:pt-8 sm:pb-32 px-4 sm:px-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest">
            <a href="/admin" className="hover:text-white">← Properties</a>
            <span>·</span>
            <span>ProCall Rentals Portfolio</span>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-2">
            1230 Anyon Street
          </h1>
          <div className="text-white/80 text-xs sm:text-sm font-medium mt-1">
            Philadelphia, PA 19125 · Row-home · 2BR / 1BA
          </div>
        </div>
      </div>

      {/* ── FLOATING HERO CARD ────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-16 sm:-mt-20 pb-16">
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] overflow-hidden">
          {/* Top strip — status + big-picture stats */}
          <div className={`h-1.5 ${theme.gradient}`}/>

          {/* Property row: photo + meta */}
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              {/* Property thumbnail */}
              <div className="relative flex-shrink-0">
                <div className="h-16 w-16 sm:h-24 sm:w-24 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-3xl sm:text-5xl">
                  🏠
                </div>
                <button className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#3B82F6] text-white text-[10px] flex items-center justify-center shadow-md hover:bg-[#2563eb]" title="Change photo">
                  ✎
                </button>
              </div>

              <div className="flex-1 min-w-0">
                {/* Status pills — inline, compact */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-[#22C6A6]/15 text-[#0F7F6A] text-[9px] font-black uppercase tracking-widest">
                    <span className="h-1 w-1 rounded-full bg-[#22C6A6]"/> Occupied
                  </span>
                  <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-[#F5B93F]/15 text-[#8A6A00] text-[9px] font-black uppercase tracking-widest">
                    🔥 2 open
                  </span>
                  <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded bg-[#3B82F6]/15 text-[#1E40AF] text-[9px] font-black uppercase tracking-widest">
                    🔗 AF #A-40219
                  </span>
                </div>

                {/* CTAs — moved up beside title */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button className="h-8 px-3 rounded-full bg-[#0F1F3A] hover:bg-[#1a3057] text-white text-[10px] font-black uppercase tracking-widest">
                    + New WO
                  </button>
                  <button className="h-8 px-3 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest">
                    Actions ⌄
                  </button>
                </div>
              </div>
            </div>

            {/* Contact icon rail — one clean row, tap to reveal */}
            <div className="mt-4 flex items-center gap-2 text-xs" data-testid="dp-property-contacts">
              <a href="https://maps.google.com/?q=1230+Anyon+St+Philadelphia+PA" target="_blank" rel="noopener noreferrer"
                className="flex-1 min-w-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-[#0F1F3A] font-bold transition-colors">
                <span className="text-[#22C6A6]">📍</span>
                <span className="truncate">1230 Anyon St</span>
              </a>
              <a href="tel:+12155551234" title="Call landlord" className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 text-[#3B82F6] text-base transition-colors">
                📞
              </a>
              <a href="mailto:manager@procall.pro" title="Email PM" className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[#B12FBB]/10 hover:bg-[#B12FBB]/20 text-[#B12FBB] text-base transition-colors">
                ✉️
              </a>
              <button title="Access notes · Lockbox 4-7-2-9" className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[#F5B93F]/15 hover:bg-[#F5B93F]/25 text-[#8A6A00] text-base transition-colors">
                🔑
              </button>
            </div>

            {/* Big-picture stats strip */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {[
                { label: "Open WOs", value: "2", color: "#F5B93F" },
                { label: "Lifetime Billed", value: "$18,420", color: "#22C6A6" },
                { label: "Avg WO Cost", value: "$387", color: "#3B82F6" },
                { label: "Last Visit", value: "2d ago", color: "#B12FBB" },
              ].map(s => (
                <div key={s.label} className="border border-slate-100 rounded-xl p-2.5 sm:p-3">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{s.label}</div>
                  <div className="text-lg sm:text-xl font-black mt-0.5" style={{ color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tab strip */}
          <div className="border-t border-slate-100 px-2 sm:px-6 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {[
                { k: "timeline", label: "Timeline", count: 47 },
                { k: "wos", label: "Work Orders", count: 24 },
                { k: "photos", label: "Photos", count: 132 },
                { k: "receipts", label: "Receipts", count: 18 },
                { k: "tenants", label: "Tenants", count: 3 },
                { k: "docs", label: "Documents", count: 7 },
                { k: "notes", label: "Notes", count: 12 },
                { k: "billing", label: "Billing", count: 9 },
              ].map(t => (
                <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`dp-property-tab-${t.k}`}
                  className={`px-3 py-3 text-xs font-black uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors ${
                    tab === t.k
                      ? "border-[#00C4CC] text-[#0F1F3A]"
                      : "border-transparent text-[#8B93A7] hover:text-[#0F1F3A]"
                  }`}>
                  {t.label}
                  <span className={`ml-1.5 text-[9px] font-black ${
                    tab === t.k ? "text-[#00C4CC]" : "text-[#8B93A7]"
                  }`}>{t.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div className="mt-4">
          {tab === "timeline" && <TimelineTab/>}
          {tab === "wos" && <WOsTab/>}
          {tab === "photos" && <PhotosTab/>}
          {tab === "receipts" && <PlaceholderTab label="Receipts"/>}
          {tab === "tenants" && <TenantsTab/>}
          {tab === "docs" && <PlaceholderTab label="Documents"/>}
          {tab === "notes" && <PlaceholderTab label="Notes"/>}
          {tab === "billing" && <PlaceholderTab label="Billing"/>}
        </div>

        <div className="mt-8 text-center">
          <p className="text-white/40 text-xs font-medium">
            Preview only · <a href="/admin/design-preview" className="text-[#00C4CC]">← back to dashboard preview</a>
          </p>
        </div>
      </div>
    </div>
    </DesignPreviewShell>
  );
}

/* ── Tab bodies ─────────────────────────────────────────── */

function TimelineTab() {
  const days = [
    {
      date: "Today · Sep 22",
      events: [
        { icon: "📦", color: "#22C6A6", text: "Parts arrived · HD boxes for kitchen faucet · Jhan", time: "12 min ago" },
        { icon: "📸", color: "#B12FBB", text: "6 before-photos uploaded · WO 3711-1 · Marcus", time: "2h ago" },
        { icon: "🕐", color: "#F5B93F", text: "Marcus clocked in · 8:42 AM", time: "8:42 AM" },
      ],
    },
    {
      date: "Yesterday · Sep 21",
      events: [
        { icon: "✅", color: "#22C6A6", text: "WO 3710-1 completed · Garbage disposal replaced · Jhan", time: "4:15 PM" },
        { icon: "💰", color: "#3B82F6", text: "Client-Safe PDF generated · $487.20 · PMR Rebill", time: "5:02 PM" },
      ],
    },
    {
      date: "Sep 15",
      events: [
        { icon: "🔗", color: "#B12FBB", text: "AppFolio sync · Service Request #3716 linked", time: "9:12 AM" },
        { icon: "📝", color: "#8B93A7", text: "Note added by Andrew: \"Tenant complained about mildew smell in basement — check next visit\"", time: "9:15 AM" },
      ],
    },
  ];
  return (
    <div className="space-y-4">
      {days.map(d => (
        <div key={d.date} className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#00C4CC]"/>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{d.date}</span>
          </div>
          <div className="divide-y divide-slate-50">
            {d.events.map((e, i) => (
              <div key={i} className="flex items-start gap-3 p-3 hover:bg-slate-50 transition-colors">
                <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
                  style={{ backgroundColor: e.color + "22" }}>
                  {e.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0F1F3A] font-medium">{e.text}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">{e.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function WOsTab() {
  const wos = [
    { wo: "3711-1", title: "Replace Garbage Disposal", status: "In Progress", color: "#FF6A2B", tech: "Jhan Rios", date: "Sep 22", cost: "—" },
    { wo: "3716-1", title: "Kitchen sink not draining", status: "Pending", color: "#F5B93F", tech: "Unassigned", date: "Sep 22", cost: "—" },
    { wo: "3710-1", title: "Bathtub caulk redo", status: "Completed", color: "#22C6A6", tech: "Marcus Hall", date: "Sep 21", cost: "$487.20" },
    { wo: "3684-1", title: "Front door lock rekey", status: "Rebilled", color: "#3B82F6", tech: "Jhan Rios", date: "Sep 15", cost: "$180.00" },
    { wo: "3641-1", title: "Basement mildew smell — inspect", status: "Paid", color: "#00C4CC", tech: "Marcus Hall", date: "Sep 08", cost: "$225.00" },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
      <div className="p-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        {["Pending 1", "In Progress 1", "Completed 1", "Rebilled 1", "Paid 1"].map(s => {
          const [label, count] = s.split(" ");
          return (
            <div key={s} className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border border-slate-200 text-[10px] font-black uppercase tracking-widest text-[#0F1F3A]">
              <span>{label}</span>
              <span className="text-[#00C4CC]">{count}</span>
            </div>
          );
        })}
      </div>
      <div className="divide-y divide-slate-50">
        {wos.map(w => (
          <div key={w.wo} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors cursor-pointer">
            <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ background: w.color }}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-black text-sm text-[#0F1F3A]">WO {w.wo}</span>
                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: w.color + "22", color: w.color }}>{w.status}</span>
              </div>
              <div className="text-sm text-[#0F1F3A] mt-0.5 truncate">{w.title}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">
                {w.tech} · {w.date}
              </div>
            </div>
            <div className="font-black text-sm text-[#22C6A6] flex-shrink-0">{w.cost}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhotosTab() {
  const photos = [
    { url: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=200", label: "Kitchen · before" },
    { url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200", label: "Kitchen · after" },
    { url: "https://images.unsplash.com/photo-1595515106969-1ce29566ff1c?w=200", label: "Sink drain" },
    { url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200", label: "Bathroom · before" },
    { url: "https://images.unsplash.com/photo-1580480055273-228ff5388ef8?w=200", label: "Bathroom · after" },
    { url: "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=200", label: "Front door" },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p, i) => (
          <div key={i} className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100">
            <img src={p.url} alt={p.label} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"/>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-white">{p.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TenantsTab() {
  const tenants = [
    { name: "Test Test", start: "2025-04-01", end: "2026-03-31", status: "Current", phone: "(215) 555-1234" },
    { name: "Sarah Williams", start: "2024-04-01", end: "2025-03-31", status: "Past", phone: "(215) 555-9876" },
    { name: "James Rodriguez", start: "2023-01-15", end: "2024-01-14", status: "Past", phone: "(215) 555-4321" },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] overflow-hidden">
      <div className="divide-y divide-slate-50">
        {tenants.map(t => (
          <div key={t.name} className="flex items-center gap-3 p-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#00C4CC] to-[#3B82F6] flex items-center justify-center text-white font-black text-sm flex-shrink-0">
              {t.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-black text-[#0F1F3A]">{t.name}</span>
                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  t.status === "Current" ? "bg-[#22C6A6]/15 text-[#0F7F6A]" : "bg-slate-100 text-[#8B93A7]"
                }`}>{t.status}</span>
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mt-0.5">
                {t.start} → {t.end} · {t.phone}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaceholderTab({ label }) {
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] p-8 text-center">
      <div className="text-5xl mb-3">🚧</div>
      <div className="font-black text-lg text-[#0F1F3A]">{label} · Preview stub</div>
      <div className="text-xs text-[#8B93A7] mt-2 max-w-md mx-auto">
        This tab would render {label.toLowerCase()} pulled from the WO detail model.
        Say <b>preview {label.toLowerCase()}</b> and I'll flesh it out.
      </div>
    </div>
  );
}
